import axios from 'axios'
import cloneDeep from 'lodash.clonedeep'
import merge from 'lodash.merge'
import okeResponse from 'okeoke.standardresponse'
import { localStorage } from 'okeoke.localstorage'
import moment from 'moment'
import { bitTest } from '../Helpers/bits'
import { OrderItemStates } from '../consts'
import {
  orderItemModifierBase
} from '../Order/orderBase.js'

function transcodeID(arr, fieldName) {
  if (arr == null) return {}
  let obj = {}
  for (let row of arr) {
    obj[row?.[fieldName]] = row
  }
  return obj
}

var OrderEngineCatalog = function (endpoint, locationsModel, app = '', deviceParams = {}) {
  this.app = app
  this.locationUUID = null
  this.dictionary = null
  this.availability = {}
  this.sizes = {}
  this.categories = {}
  this.categoryTree = []
  this.locationCategoryTree = {}
  this.currencies = {}
  this.assets = {}
  this.languages = {}
  this.items = {}
  this.modifiers = {}
  this.modifierGroups = {}
  this.upsaleTemplates = {}
  this.catalog = {}
  this.packages = {}
  this.vatGroups = {}
  this.counters = {}
  this.eanIndex = {}
  this.pluIndex = {}
  this.sizeGroups = {}
  this.sizeConnections = {}
  this.layouts = {}
  this.catalogState = ''
  this.timestamp = null
  this.updateInterval = null

  this.callbacks = {
    onCatalogStateChange: {},
    onDictionaryLoaded: {},
    onLocationAvailabilityUpdated: {},
    onLocationItemUpdated: {},
    onDiscountsUpdated: {}
  }

  this.setCatalogData = (catalogData) => {
    this.timestamp = catalogData.timestamp
    this.sizes = catalogData.sizes
    this.categories = catalogData.categories
    this.categoryTree = catalogData.categoryTree
    this.currencies = catalogData.currencies
    this.assets = catalogData.assets
    this.languages = catalogData.languages
    this.items = catalogData.items
    this.modifiers = catalogData.modifiers
    this.modifierGroups = catalogData.modifierGroups
    this.upsaleTemplates = catalogData.upsaleTemplates
    this.availability = catalogData.availability
    this.packages = catalogData.packages
    this.vatGroups = catalogData.vatGroups
    this.counters = catalogData.counters
    this.tables = catalogData.tables
    this.discounts = catalogData.discounts
    this.depositTypes = catalogData.depositTypes || {}
    this.assembledItems = {}
    this.eanIndex = {}
    this.pluIndex = {}
    this.sizeGroups = catalogData.sizeGroups || {}
    this.sizeConnections = catalogData.sizeConnections || {}
    this.layouts = catalogData.layouts || {}
    this.indexEanAndPlu()
    this.assembleAllItemsBase()
    this.populateVirtualCategories()
    this.tagItems = {
      forcedUpsellTA: this.findItemsByTags(['forcedUpsellTA']),
      forcedUpsell: this.findItemsByTags(['forcedUpsell'])
    }
    this.assembleDiscounts()
  }

  this.indexEanAndPlu = () => {
    for (let size of Object.values(this.sizes)) {
      if (size.ean && size.ean.length > 0) {
        this.eanIndex[size.ean] = { t: 1, i: size.itemUUID, s: size.uuid }
      }
      if (size.plu > 0) {
        this.pluIndex[size.plu] = { t: 1, i: size.itemUUID, s: size.uuid }
      }
    }
  }

  this.getCatalog = async (forceRefresh) => {
    if (this.catalogState !== 'Loading') {
      if (this.catalogState !== 'Loaded' || forceRefresh || this.timestamp == null) {
        if (this.catalogState !== 'Loaded') this.callCallbacks('onCatalogStateChange', { state: 'Loading' })
        const axiosconfig = {
          params: {
            dataNeeded: {
              all: 1
            }
          }
        }
        try {
          const response = await axios.get(endpoint + '/api/v1/order/getCatalog', axiosconfig)
          if (response.data?.success) {
            this.setCatalogData(response.data)
            this.checkAllCategoriesAvailabilityAllLocations()
            if (this.catalogState !== 'Loaded') this.callCallbacks('onCatalogStateChange', { state: 'Loaded' })
            return response.data
          } else {
            console.log('Error loading catalog I:', JSON.stringify(response.data))
          }
        } catch (error) {
          console.log('Error loading catalog II:', error)
        }
      }
    }
  }

  this.assembleDiscounts = () => {
    for (let discount of Object.values(this.discounts)) {
      let icatArray = Object.entries(discount.includeCategories || {})
      if (icatArray.length > 0) {
        let includeSizes = []
        for (let [key, value] of icatArray) {
          //console.log(key, this.categories)
          let category = this.categories?.[key.toUpperCase()] || null
          for (let item of category?.items || []) {
            includeSizes = includeSizes.concat(this.items[item.uuid]?.sizes || [])
          }
        }
        includeSizes = transcodeID(includeSizes, 'uuid')
        if (Object.entries(discount.includeSizes || {}).length > 0) {
          discount.includeSizes = merge(includeSizes, discount.includeSizes)
        } else {
          discount.includeSizes = includeSizes
        }
      }

      let ecatArray = Object.entries(discount.excludeCategories || {})
      if (ecatArray.length > 0) {
        let excludeSizes = []
        for (let [key] of ecatArray) {
          let category = this.categories?.[key.toUpperCase()] || null
          for (let item of category?.items || []) {
            excludeSizes = excludeSizes.concat(this.items[item.uuid]?.sizes || [])
          }
        }
        excludeSizes = transcodeID(excludeSizes, 'uuid')
        discount.excludeSizes = merge((discount.excludeSizes || {}), excludeSizes)
      }
    }
    this.checkDiscountsValidity(true)
  }

  function checkValidNow(discount) {
    if (discount == null) return false
    let enabled = discount.enabled ? true : false
    let validFrom = discount?.validFrom ? moment().isAfter(moment(discount?.validFrom)) : true
    let validUntil = discount?.validUntil ? moment().isBefore(moment(discount?.validUntil)) : true
    let day = moment().day()
    if (day === 0) day = 7 // Sunday is 0 in momentjs, but 7 in the database
    let hour = moment().hour()
    let minute = moment().minute()
    let checkBit = Math.floor(minute / 15)
    let validTimerange = bitTest(parseInt(discount?.validity?.days?.[day]?.[hour], 16), checkBit)
    let isValid = enabled && validTimerange && validFrom && validUntil
    return { isValid, checks: { validTimerange, validFrom, validUntil, enabled } }
  }

  this.getDiscount = (id) => {
    let discount = this.discounts?.[id] || null
    if (discount) {
      let valid = checkValidNow(discount)
      discount.valid = valid.isValid
      discount.checks = valid.checks
    }
    return discount
  }

  this.checkDiscountsValidity = (force = false) => {
    const prevDiscounts = cloneDeep(this.discounts)
    for (let discount of Object.values(this.discounts)) {
      let valid = checkValidNow(discount)
      discount.valid = valid.isValid
      discount.checks = valid.checks
    }
    if (this.updateInterval == null || !force) {
      this.updateInterval = setTimeout(() => {
        this.checkDiscountsValidity()
      }, 60000)
    }
    let changed = false
    try {
      changed = JSON.stringify(prevDiscounts || {}) !== JSON.stringify(this.discounts || {})
    } catch (e) {
      console.error(e)
    }
    if (changed || force) {
      this.callCallbacks('onDiscountsUpdated', {})
    }
  }

  let compareByName = (a, b, langCode) => {
    const aName = this.items[a.uuid].name[langCode].toUpperCase()
    const bName = this.items[b.uuid].name[langCode].toUpperCase()
    let comparison = 0
    if (aName > bName) {
      comparison = 1
    } else if (aName < bName) {
      comparison = -1
    }
    return comparison
  }

  let compareByPopularity = (a, b) => {
    return this.items[b.uuid].idx - this.items[a.uuid].idx
  }

  let compareByPriceInc = (a, b) => {
    return this.items[a.uuid].price - this.items[b.uuid].price
  }

  let compareByPriceDec = (a, b) => {
    return this.items[b.uuid].price - this.items[a.uuid].price
  }

  let checker = (arr, target) => {
    if (target == null) return true
    if (arr == null) return false
    return target.every(v => arr.includes(v))
  }

  this.getCategoryItems = (catUUID, filter = '', langCode = 'hu', tags = [], maxCount = 0, fromIndex = 0, sortBy = 'popularity') => {
    filter = filter.toUpperCase()
    let items = []
    let category = this.categories[catUUID]
    let index = 0
    let count = 0
    let itemsToFilter = []
    if (catUUID == null) itemsToFilter = Object.values(this.items)
    if (category != null) itemsToFilter = category.items
    switch (sortBy) {
      case 'popularity': itemsToFilter = itemsToFilter.sort(compareByPopularity)
        break
      case 'priceInc': itemsToFilter = itemsToFilter.sort(compareByPriceInc)
        break
      case 'priceDec': itemsToFilter = itemsToFilter.sort(compareByPriceDec)
        break
      case 'name': itemsToFilter = itemsToFilter.sort(function (a, b) { return compareByName(a, b, langCode) })
        break
    }
    for (let itemUUID of itemsToFilter) {
      let item = this.items[itemUUID.uuid]
      let filterOK = (filter !== '' ? item.name[langCode].toUpperCase().includes(filter) : true)
      let tagFilterOK = (tags != null && tags.length > 0 ? checker(item?.tags, tags) : true)
      if (filterOK && tagFilterOK) {
        if (index >= fromIndex) {
          if (maxCount === 0 || count < maxCount) {
            items.push({
              uuid: item.uuid,
              name: item.name[langCode],
              price: item.price
            })
            count++
          } else {
            if (maxCount > 0 && count >= maxCount) {
              return items // Exit early
            }
          }
        }
        index++
      }
    }
    return items
  }

  this.getItemsFiltered = (catUUID, filter = '', langCode = 'hu', tags = [], maxCount = 0, fromIndex = 0, sortBy = 'popularity') => {
    filter = filter.toUpperCase()
    let items = []
    let category = this.categories[catUUID]
    let index = 0
    let count = 0
    let itemsToFilter = []
    if (catUUID == null) itemsToFilter = Object.values(this.items)
    if (category != null) itemsToFilter = category.items
    switch (sortBy) {
      case 'popularity': itemsToFilter = itemsToFilter.sort(compareByPopularity)
        break
      case 'priceInc': itemsToFilter = itemsToFilter.sort(compareByPriceInc)
        break
      case 'priceDec': itemsToFilter = itemsToFilter.sort(compareByPriceDec)
        break
      case 'name': itemsToFilter = itemsToFilter.sort(function (a, b) { return compareByName(a, b, langCode) })
        break
    }
    let availableTags = []
    for (let itemUUID of itemsToFilter) {
      let item = this.items[itemUUID.uuid]
      let filterOK = (filter !== '' ? item.name[langCode].toUpperCase().includes(filter) : true)
      let tagFilterOK = (tags != null && tags.length > 0 ? checker(item?.tags, tags) : true)
      if (filterOK && tagFilterOK) {
        if (index >= fromIndex) {
          if (maxCount === 0 || count < maxCount) {
            availableTags = merge(availableTags, item?.tags)
            items.push({
              uuid: item.uuid,
              name: item.name[langCode],
              price: item.price
            })
            count++
          } else {
            if (maxCount > 0 && count >= maxCount) {
              return okeResponse(true, 200, 'OK', { items, availableTags }) // Exit early
            }
          }
        }
        index++
      }
    }
    return okeResponse(true, 200, 'OK', { items, availableTags })
  }

  this.getItemsOfCategories = (categories = null) => {
    if (categories == null) {
      return Object.values(this.items || {}).map(item => { return { uuid: item.uuid } })
    } else {
      let items = []
      //console.log(this.categories)
      for (let catUUID of categories) {
        items = items.concat(this.categories[catUUID].items)
      }
      let uniqueItems = transcodeID(items, 'uuid')
      return Object.values(uniqueItems)
    }
  }

  this.populateVirtualCategory = (category) => {
    category.items = this.getItemsOfCategories(category.virtualFilter?.categories)
    if (category.virtualFilter?.tags != null) {
      let filteredItems = []
      for (let item of category.items) {
        if (checker(this.items[item.uuid]?.tags, category.virtualFilter?.tags)) {
          filteredItems.push(item)
        }
      }
      category.items = filteredItems
    }
    return category
  }

  this.findItemsByTags = (tags) => {
    if (tags != null) {
      let filteredItems = []
      for (let item of Object.values(this.items)) {
        if (checker(item?.tags, tags)) {
          filteredItems.push(item.uuid)
        }
      }
      return filteredItems
    }
    return []
  }

  this.getForcedUpsells = (takeAway, locationUUID) => {
    let items = (takeAway ? this.tagItems?.['forcedUpsellTA'] : this.tagItems?.['forcedUpsell']) || []
    let final = []
    for (let itemUUID of items) {
      let availability = this.assembledItems[locationUUID][itemUUID].availability
      if (availability.visible && availability.available) {
        final.push(itemUUID)
      }
    }
    if (final.length === 0) return null
    return final
  }

  this.populateVirtualCategories = () => {
    for (let category of Object.values(this.categories || {})) {
      if (category.virtual) this.populateVirtualCategory(category)
    }
  }

  this.checkCategoryAvailability = (category, locationUUID) => {
    let availability = {
      visibleItems: 0,
      availableItems: 0,
      visibleSubItems: 0,
      availableSubItems: 0,

      visibleFilteredItems: 0,
      availableFilteredItems: 0,
      visibleFilteredSubItems: 0,
      availableFilteredSubItems: 0,
    }
    if (category.categories != null) {
      for (let cat of (category?.categories || [])) {
        let subCat = this.checkCategoryAvailability(cat, locationUUID)
        availability.visibleSubItems += subCat.visibleItems
        availability.availableSubItems += subCat.availableItems
        availability.visibleFilteredSubItems += subCat.visibleFilteredItems
        availability.availableFilteredSubItems += subCat.availableFilteredItems
      }
    }
    if (this.categories?.[category.uuid] != null) {
      for (let item of (this.categories?.[category.uuid]?.items || [])) {
        try {
          let itemVisibility = this.assembledItems?.[locationUUID][item.uuid].availability || {}
          availability.visibleItems += (itemVisibility.visible == null ? 0 : itemVisibility.visible)
          availability.availableItems += (itemVisibility.available == null ? 0 : itemVisibility.available)
          availability.visibleFilteredItems += ((itemVisibility.visible && itemVisibility.filterOK) ? 1 : 0)
          availability.availableFilteredItems += ((itemVisibility.available && itemVisibility.filterOK) ? 1 : 0)
        } catch (e) { }
      }
    }
    category.availability = availability
    return availability
  }

  this.checkAllCategoriesAvailability = (locationUUID) => {
    let locationCategoryTree = cloneDeep(this.categoryTree)
    for (let category of (locationCategoryTree || [])) {
      this.checkCategoryAvailability(category, locationUUID)
    }
    this.locationCategoryTree[locationUUID] = locationCategoryTree
    this.callCallbacks('onLocationAvailabilityUpdated', { locationUUID })
  }

  this.checkAllCategoriesAvailabilityAllLocations = () => {
    let locations = locationsModel.locations
    if (locations?.length > 0) {
      for (let location of (locations || [])) {
        this.checkAllCategoriesAvailability(location.uuid)
      }
    }
  }

  this.assembleAllItemsBaseLocation = (locationUUID) => {
    try {
      let location = locationsModel.getLocation(locationUUID)
      let assembledItems = {}
      for (let item of Object.values(this.items)) {
        let assembledItem = this.assembleItem(item.uuid, true, locationUUID, null, null, true, location)
        assembledItems[item.uuid] = assembledItem
      }
      return { assembledItems }
    } catch (e) {
      console.log(e)
      return null
    }
  }

  this.assembleAllItemsBase = () => {
    for (let locationUUID of Object.keys((this.availability || {}))) {
      let itemsConcat = this.assembleAllItemsBaseLocation(locationUUID)
      this.assembledItems[locationUUID] = itemsConcat.assembledItems
    }
  }

  this.getDictionary = async () => {
    if (this.dictionary != null) return this.dictionary
    try {
      const response = await axios.get(endpoint + '/api/v1/dictionary/' + this.app)
      if (response.data.success) {
        localStorage.setItem(`okeoke-${this.app}-dictionary`, JSON.stringify(response.data))
        this.dictionary = response.data
        this.callCallbacks('onDictionaryLoaded', true)
      }
      return response.data
    } catch (error) {
      let backupDictionary = localStorage.getItem(`okeoke-${this.app}-dictionary`)
      try {
        this.dictionary = JSON.parse(backupDictionary)
        return this.dictionary
      } catch (e) {
        return {}
      }
    }
  }

  this.checkItemAvailable = (itemUUID, enabled = 1, locationUUID = null, extID = null) => {
    const locationToCheck = locationUUID != null ? locationUUID : this.locationUUID
    let location = locationsModel.getLocation(locationToCheck)
    let res = {
      visible: 0,
      available: 0,
      stock: 0
    }
    if (enabled === 0) return res
    if (this.availability?.[locationToCheck] != null && this.availability[locationToCheck]?.[itemUUID] != null) {
      res.visible = this.availability[locationToCheck][itemUUID].visible || 0

      res.available = this.availability[locationToCheck][itemUUID].available || 0
      res.stock = this.availability[locationToCheck][itemUUID].stock || 0
      let hasOMExtID = ((this.availability[locationToCheck][itemUUID].omsid > 0 && this.availability[locationToCheck][itemUUID].omsextid) || (+this.availability[locationToCheck][itemUUID].omsid === 0 && extID != null && extID !== ''))
      if ((!hasOMExtID) && location?.useExternalOrderManager) {
        res.visible = 0
      }
      if (hasOMExtID) {
        res.extID = (this.availability[locationToCheck][itemUUID].omsid > 0 && this.availability[locationToCheck][itemUUID].omsextid) ? this.availability[locationToCheck][itemUUID].omsextid : extID
      }
    }

    return res
  }

  this.updateItemAvailability = (itemUUID, locationUUID, visible, available) => {
    if (this.availability?.[locationUUID] == null) return
    this.availability[locationUUID][itemUUID] = {
      ...this.availability[locationUUID][itemUUID],
      visible,
      available
    }
    let isSize = this.sizes[itemUUID] != null
    //console.log(isSize, this.sizes[itemUUID]?.items)
    if (isSize) {
      for (let item of this.sizes[itemUUID].items || []) {
        let assembledItem = this.assembleItem(item, true, locationUUID)
        this.assembledItems[locationUUID][item] = assembledItem
        this.callCallbacks('onLocationItemUpdated', { locationUUID, itemUUID: item })
      }
    } else {
      let isModifier = this.modifiers[itemUUID] != null
      if (isModifier) {
        for (let item of this.modifiers[itemUUID].items || []) {
          let assembledItem = this.assembleItem(item, true, locationUUID)
          this.assembledItems[locationUUID][item] = assembledItem
          this.callCallbacks('onLocationItemUpdated', { locationUUID, itemUUID: item })
        }
      }
    }
    this.checkAllCategoriesAvailability(locationUUID)
  }

  this.refreshItemAvailability = (item, locationUUID) => {
    item.visibleModifierGroups = 0
    item.availableModifierGroups = 0
    item.availability = this.assembledItems[locationUUID][item.uuid].availability
  }

  this.mergeCustomValues = (item, locationUUID, sizeUUID) => {
    if (this.items?.[item.uuid].customValues?.[locationUUID]?.[sizeUUID] != null) {
      merge(item, this.items?.[item.uuid].customValues?.[locationUUID]?.[sizeUUID])
    }
  }

  this.getPriceCustomValue = (item, locationUUID, sizeUUID) => {
    if (this.items?.[item.uuid].customValues?.[locationUUID]?.[sizeUUID] != null) {
      return this.items[item.uuid].customValues[locationUUID][sizeUUID].price || 0
    } else {
      return 0
    }
  }

  const checkModifierGroupValidity = (modGroup) => {
    let avaField = (this.app === 'pos' ? 'available' : 'visible')

    // If modifier group is unavailable default to valid
    if (!modGroup.availability?.[avaField]) {
      modGroup.valid = true
      return true
    }

    // Check if the minimum selected modifiers rule is possible to follow. If not, the group is valid by default.
    if (modGroup.min > 0) {
      let maxSelectable = 0
      for (let mod of modGroup.modifiers) {
        if (mod.availability?.[avaField]) {
          maxSelectable += +(mod.max || modGroup.min)
        }
      }
      if (maxSelectable < modGroup.min) {
        modGroup.valid = true
        return true
      }
    }
    let count = 0
    for (let mod of (modGroup?.modifiers || [])) {
      mod.valid = true
      if (mod.availability?.[avaField] && mod.count > 0) {
        count += mod.count
      }
    }
    if (count >= modGroup.min && count <= (modGroup.max || 9999999)) {
      modGroup.valid = true
    } else {
      modGroup.valid = false
    }
    return modGroup.valid
  }

  this.checkItemValidity = (item) => {
    let valid = true
    for (let modGroup of (item?.modifierGroups || [])) {
      if (!checkModifierGroupValidity(modGroup)) {
        valid = false
      }
    }
    item.valid = valid
    return valid
  }

  this.calculateItemPrices = (item, order, _locationUUID = null, firstRun = false) => {
    if (item == null) return okeResponse(false, 500, 'Error', null)
    try {
      let quantityMultiplier = 1
      let scaledItem = (item?.sizeAttributes?.scale && ['kg', 'liter'].includes(item.unitType))
      if (scaledItem) {
        quantityMultiplier = +item.quantity > 0 ? +item.quantity / +item.quantityBase : 0
      }
      let realCount = item.state !== OrderItemStates.STORNO ? (item.count || 0) : 0
      let orderTakeAway = order?.takeAway || 0
      let itemTakeAway = (item.takeAway == null ? orderTakeAway : item.takeAway)
      if ((order?.insertDeviceType || 0) <= 1000) {
        item.packagePrice = (itemTakeAway ? +(item?.package?.priceTA || 0) : +(item?.package?.price || 0))
        item.vat = itemTakeAway ? this.vatGroups?.[item.vatGroupID]?.vatTA || '27.00' : this.vatGroups?.[item.vatGroupID]?.vat || '27.00'
      } else {
        item.packagePrice = +(item?.packagePrice || 0)
        item.vat = item.vat || '27.00'
      }
      let sumPriceUnit = +item.price
      //if(item.selectedSizeUUID === "11EF7A44CE860870B6DA97482EA3B7D9") console.log('1: ', sumPriceUnit)
      let basePriceUnit = sumPriceUnit
      if (scaledItem && sumPriceUnit > 0) {
        sumPriceUnit = sumPriceUnit * quantityMultiplier
      }
      //if(item.selectedSizeUUID === "11EF7A44CE860870B6DA97482EA3B7D9") console.log('2: ', sumPriceUnit)
      for (let modgroup of (item?.modifierGroups || [])) {
        //if(item.selectedSizeUUID === "11EF7A44CE860870B6DA97482EA3B7D9") console.log(modgroup)
        for (let modifier of modgroup.modifiers || []) {
          if (modifier.count && modifier.price != null) {
            let price = +modifier.count * +modifier.price
            basePriceUnit += price
            if (scaledItem && modifier?.attributes?.scaled && price > 0) {
              price = price * quantityMultiplier
            }
            sumPriceUnit += price
          }
        }
      }
      //if(item.selectedSizeUUID === "11EF7A44CE860870B6DA97482EA3B7D9") console.log('3: ', sumPriceUnit)
      sumPriceUnit = +(sumPriceUnit > 0 ? sumPriceUnit.toFixed(2) : 0)
      item.sumPriceUnit = sumPriceUnit
      item.basePriceUnit = +(basePriceUnit > 0 ? basePriceUnit.toFixed(2) : 0)
      item.sumPrice = sumPriceUnit * realCount

      let connectedSizesPrice = 0
      if(item?.sizeAttributes?.menuSize) {
        if(firstRun) {
          connectedSizesPrice = this.calculateMenuSizePrice(item.selectedSizeUUID, order?.locationUUID || _locationUUID)
        }
        //console.log('Connected sizes price:', connectedSizesPrice)
      }

      item.totalPriceUnit = sumPriceUnit + item.packagePrice + +item.depositPrice + connectedSizesPrice
      item.totalPrice = Math.round(item.totalPriceUnit * realCount)
      // if(item.selectedSizeUUID === "11EF7A44CE860870B6DA97482EA3B7D9") {
      //   console.log({
      //     sumPriceUnit,
      //     packagePrice: item.packagePrice,
      //     depositPrice: +item.depositPrice,
      //     connectedSizesPrice
      //   })
      //   console.log('Base price:', item.name, item.sizeName, sumPriceUnit, item.totalPriceUnit )
      // }
      return okeResponse(true, 200, 'OK', null)
    } catch (e) {
      console.log(e)
      return okeResponse(false, 500, 'Error', null)
    }
  }

  this.getDefaultSizeUUID = (item, locationUUID) => {
    let avaField = (this.app === 'pos' ? 'available' : 'visible')
    let defaultSize = this.sizes?.[item.defaultSize]
    let availability = this.checkItemAvailable(defaultSize?.uuid || '', 1, locationUUID, defaultSize?.extID)
    if (availability?.[avaField]) {
      return defaultSize?.uuid || ''
    }
    for (let size of item.sizes) {
      let availability = this.checkItemAvailable(size?.uuid || '', 1, locationUUID, size?.extID)
      if (availability?.[avaField]) {
        return size.uuid
      }
    }
    return null
  }

  function cleanOrderItemModifier(modifier) {
    for (let key in modifier) {
      if (!(key in orderItemModifierBase)) {
        //console.log('DEL MODIFIER KEY:', key)
        delete modifier[key]
      }
    }
    return modifier
  }

  this.calculateMenuSizePrice = (sizeUUID, locationUUID) => {
    //console.log(sizeUUID, locationUUID)
    let price = 0
    let sizeConnections = this.sizeConnections[sizeUUID]
    for(let connection of sizeConnections?.g || []) {
      let sizeGroup = this.sizeGroups[connection.uuid]
      for(let member of sizeGroup.members) {
        let size = this.sizes[member.uuid]
        if(member.defaultCount > 0) {
          
          let assembledMember = this.assembleItem(size.itemUUID, true, locationUUID, size.uuid, null, true, null, +member.price)
          if(assembledMember.price !== assembledMember.totalPriceUnit) {
            //console.log(assembledMember.price, assembledMember.totalPriceUnit, +member.price)
            //console.log(assembledMember.selectedSizeUUID)
          }
          price += assembledMember.totalPriceUnit * member.defaultCount
          //console.log(member.price, price)
        }
      }
    }
    return price
  }

  this.assembleItemObject = (item, assignDefaultValues = true, locationUUID, sizeUUID = null, order = null, firstRun = false, location = null, _basePrice = null) => {
    try {
      let avaField = (['pos', 'orderManager', 'orderScreen'].includes(this.app) ? 'available' : 'visible')
      if (location == null) {
        location = locationsModel.getLocation(locationUUID)
      }
      item.selectedSizeUUID = (sizeUUID != null ? sizeUUID : this.getDefaultSizeUUID(item, locationUUID))
      item.visibleModifierGroups = 0
      item.availableModifierGroups = 0
      item.visibleSizes = 0
      item.availableSizes = 0
      item.availability = {}
      item.package = this.packages?.[item.packageID] || null
      item.name = this.items?.[item.uuid]?.name
      let size = cloneDeep(this.sizes?.[item.selectedSizeUUID])
      item.sizeName = size?.name
      item.sizeExtID = size?.extID
      // Package override if set in size
      if(size?.packageID) {
        item.package = this.packages?.[size.packageID] || null
      } else {
        if(size?.packageID === 0) {
          item.package = null
        }
      }
      let depositType = this.depositTypes?.[size?.depositTypeID]
      item.depositPrice = +depositType?.price || 0
      item.depositVat = depositType?.vat || 0
      item.depositType = depositType ? { ...depositType } : null
      if (!item.enabled || item.selectedSizeUUID == null) return item
      for (let modgroup of (item?.modifierGroups || [])) {
        modgroup = merge(modgroup, this.modifierGroups[modgroup.uuid])
        modgroup.visibleModifiers = 0
        modgroup.availableModifiers = 0
        modgroup.availability = {}
        let modsCount = 0
        let firstAvailableMod = null
        for (let mod of (modgroup?.modifiers || [])) {
          mod = merge(mod, this.modifiers[mod.uuid])
          //if(mod.count > 0) console.log(mod)
          // Clean modifier object
          if (firstRun) {
            if (this.modifiers[mod.uuid].items == null) this.modifiers[mod.uuid].items = []
            if (!this.modifiers[mod.uuid].items.includes(item.uuid)) {
              this.modifiers[mod.uuid].items.push(item.uuid)
            }
          }
          //cleanOrderItemModifier(mod)

          mod.availability = this.checkItemAvailable(mod.uuid, mod.enabled, locationUUID, mod.extID)
          if (mod.availability?.[avaField] && firstAvailableMod == null) {
            firstAvailableMod = mod
          }
          modgroup.visibleModifiers += mod.availability.visible
          modgroup.availableModifiers += mod.availability.available
          if (assignDefaultValues) {
            if (mod.availability?.[avaField]) {
              mod.count = mod.defaultCount || 0
              if (mod.min > mod.count) mod.count = mod.min
            } else {
              mod.count = 0
            }
            modsCount += (mod.count || 0)
          }
        }
        if (assignDefaultValues && firstAvailableMod && +modgroup.type === 1 && +modsCount === 0) {
          firstAvailableMod.count = 1
        }
        modgroup.availability.available = ((modgroup.availableModifiers > 0 && modgroup.enabled) ? 1 : 0)
        modgroup.availability.visible = ((modgroup.visibleModifiers > 0 && modgroup.enabled) ? 1 : 0)
        item.visibleModifierGroups += modgroup.availability.visible
        item.availableModifierGroups += modgroup.availability.available
      }
      for (let size of (item?.sizes || [])) {
        size = merge(size, this.sizes[size.uuid])
        size.price = this.getPriceCustomValue(item, locationUUID, size.uuid)
        size.availability = this.checkItemAvailable(size.uuid, 1, locationUUID, size.extID)
        item.visibleSizes += size.availability.visible
        item.availableSizes += size.availability.available
        if (firstRun) {
          if (this.sizes[size.uuid] != null) {
            if (this.sizes?.[size.uuid]?.items == null) this.sizes[size.uuid].items = []
            if (!this.sizes[size.uuid].items.includes(item.uuid)) {
              this.sizes[size.uuid].items.push(item.uuid)
            }
          }
        }
      }
      if (item.selectedSizeUUID != null) {
        this.mergeCustomValues(item, locationUUID, item.selectedSizeUUID)
      }
      item.quantity = item.quantity != null ? +item.quantity : +this.sizes?.[item.selectedSizeUUID]?.unit
      item.quantityBase = +this.sizes?.[item.selectedSizeUUID]?.unit || 1
      item.unitType = this.sizes?.[item.selectedSizeUUID]?.unitType || 'piece'
      item.sizeAttributes = this.sizes?.[item.selectedSizeUUID]?.attributes || {}
      item.availability.available = ((item.availableSizes > 0 && item.enabled) ? 1 : 0)
      item.availability.visible = ((item.visibleSizes > 0 && item.enabled) ? 1 : 0)

      if(_basePrice != null) item.price = _basePrice
      //deviceParams
      if (deviceParams?.prepCounters?.length > 0) {
        let itemPrepCounters = Object.values(item.prep || {})
        let match = (itemPrepCounters.length === 0)
        if (!match) {
          for (let prepCounter of itemPrepCounters) {
            if (deviceParams.prepCounters.includes(prepCounter.id)) {
              match = true
            }
          }
        }
        item.availability.filterOK = (match ? 1 : 0)
      } else {
        item.availability.filterOK = 1
      }
      this.checkItemValidity(item)
      this.calculateItemPrices(item, order, locationUUID, firstRun)
    } catch (e) {
      console.error(e)
    }
    return item
  }

  this.assembleItem = (itemUUID, assignDefaultValues = true, locationUUID, sizeUUID = null, order = null, firstRun = false, location = null, _basePrice) => {
    if (this.items == null || this.items[itemUUID] == null) return null
    if (location == null) {
      location = locationsModel.getLocation(locationUUID)
    }
    let item = cloneDeep(this.items[itemUUID])
    //console.log(item)
    return this.assembleItemObject(item, assignDefaultValues, locationUUID, sizeUUID, order, firstRun, location, _basePrice)
  }

  this.getItemUpsales = (uuid, locationUUID, isVisible) => {
    let suggestions = {}
    try {
      for (let upsale of this.items[uuid].upsaleTemplates) {
        if (this.upsaleTemplates[upsale.upsaleTemplateUUID] != null && this.upsaleTemplates[upsale.upsaleTemplateUUID].enabled) {
          suggestions = merge(suggestions, transcodeID(this.upsaleTemplates[upsale.upsaleTemplateUUID].items, 'itemUUID'))
        }
      }
      for (let itemUUID of Object.keys(suggestions)) {
        let availability = this.assembledItems?.[locationUUID]?.[itemUUID]?.availability
        if (!availability.available || !(isVisible ? availability.visible : true)) {
          delete suggestions[itemUUID]
        }
      }
      return suggestions
    } catch (e) {
      console.log(e)
      return {}
    }
  }

  this.addCallback = (cbName, name, func) => {
    try {
      this.callbacks[cbName][name] = func
    } catch (e) {
      console.error('Invalid callback name', cbName)
    }
  }

  this.removeCallback = (cbName, name) => {
    try {
      delete this.callbacks[cbName][name]
    } catch (e) {
      console.error('Invalid callback name', cbName)
    }
  }

  this.callCallbacks = async (cbName, message) => {
    for (let [key, callback] of Object.entries(this.callbacks[cbName])) {
      if (typeof callback === 'function') {
        try {
          if (cbName === 'onCatalogStateChange') {
            this.catalogState = message.state
          }
          callback(message)
        } catch (e) {
          console.error(e)
        }
      }
    }
  }
}

export { OrderEngineCatalog }
